import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { getBlockColor, TETROMINO_COLORS, SPECTRUM_COLORS } from '../SpectrumMaterial';

// Rainbow spectrum colors for prismatic effects
const SPECTRUM_RAINBOW = [
  '#FF3030', // Red
  '#FF8C00', // Orange
  '#FFD700', // Yellow
  '#32CD32', // Green
  '#00CED1', // Cyan
  '#4169FF', // Blue
  '#9B30FF', // Violet
];

// Smooth easing functions
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const easeInOutQuad = (t) => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
const easeOutQuint = (t) => 1 - Math.pow(1 - t, 5);

// --- Prismatic Beam: white light sweeps across, splitting blocks into color ---
const PrismaticBeam = ({ row, delay }) => {
  const beamRef = useRef();
  const trailRef = useRef();
  const startTime = useRef(null);

  useFrame((state) => {
    if (!beamRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - delay;

    if (adjustedTime <= 0 || adjustedTime > 0.9) {
      beamRef.current.visible = false;
      if (trailRef.current) trailRef.current.visible = false;
      return;
    }

    beamRef.current.visible = true;
    if (trailRef.current) trailRef.current.visible = true;

    const progress = adjustedTime / 0.9;
    const easedProgress = easeOutCubic(progress);

    // Beam sweeps left to right
    const xPos = -6 + easedProgress * 12;
    beamRef.current.position.x = xPos;

    // Bright leading edge that narrows as it moves
    const intensity = Math.sin(progress * Math.PI);
    beamRef.current.material.opacity = 0.95 * intensity;
    beamRef.current.scale.x = 0.8 + intensity * 1.2;
    beamRef.current.scale.y = 1.0 + intensity * 0.3;

    // Rainbow trail behind the beam
    if (trailRef.current) {
      trailRef.current.position.x = xPos - 1.5;
      trailRef.current.scale.x = 3 + easedProgress * 4;
      trailRef.current.material.opacity = 0.5 * intensity;
    }
  });

  const yPos = 20 - row - 0.5;

  return (
    <>
      {/* Bright white leading beam */}
      <mesh ref={beamRef} position={[-6, yPos, 0.4]} visible={false}>
        <planeGeometry args={[0.4, 1.05]} />
        <meshBasicMaterial
          color={0xffffff}
          transparent
          opacity={0.95}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>
      {/* Rainbow gradient trail */}
      <mesh ref={trailRef} position={[-6, yPos, 0.35]} visible={false}>
        <planeGeometry args={[1, 1.0]} />
        <meshBasicMaterial
          color={SPECTRUM_RAINBOW[row % SPECTRUM_RAINBOW.length]}
          transparent
          opacity={0.5}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>
    </>
  );
};

// --- Chromatic Dissolve Block: block cycles through spectrum colors then dissolves into light ---
const ChromaticDissolveBlock = ({ position, color, delay }) => {
  const meshRef = useRef();
  const glowRef = useRef();
  const startTime = useRef(null);

  const rainbowColors = useMemo(() =>
    SPECTRUM_RAINBOW.map(c => new THREE.Color(c)),
  []);

  const baseColor = useMemo(() => new THREE.Color(color), [color]);

  useFrame((state) => {
    if (!meshRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - delay;

    // Phase 1: Chromatic cycle (0-0.5s) - block rapidly shifts through rainbow
    // Phase 2: White flash + dissolve (0.5-0.8s) - turns to pure light
    // Phase 3: Fade away (0.8-1.05s)

    if (adjustedTime <= 0) {
      meshRef.current.visible = false;
      if (glowRef.current) glowRef.current.visible = false;
      return;
    }

    if (adjustedTime > 1.05) {
      meshRef.current.visible = false;
      if (glowRef.current) glowRef.current.visible = false;
      return;
    }

    meshRef.current.visible = true;

    if (adjustedTime < 0.5) {
      // Phase 1: Chromatic cycling
      const cycleProgress = adjustedTime / 0.5;
      const speed = 3 + cycleProgress * 20; // Accelerating color cycle
      const colorIndex = Math.floor(state.clock.elapsedTime * speed) % rainbowColors.length;
      const nextIndex = (colorIndex + 1) % rainbowColors.length;
      const t = (state.clock.elapsedTime * speed) % 1;

      const currentColor = rainbowColors[colorIndex].clone().lerp(rainbowColors[nextIndex], t);

      // Blend from base color to cycling rainbow
      const blendedColor = baseColor.clone().lerp(currentColor, easeInOutQuad(cycleProgress));

      meshRef.current.material.color.copy(blendedColor);
      meshRef.current.material.emissive.copy(blendedColor);
      meshRef.current.material.emissiveIntensity = 0.8 + cycleProgress * 2.5;
      meshRef.current.material.opacity = 0.95;

      // Growing glow
      if (glowRef.current) {
        glowRef.current.visible = true;
        glowRef.current.material.color.copy(blendedColor);
        glowRef.current.material.opacity = 0.3 * cycleProgress;
        glowRef.current.scale.setScalar(1 + cycleProgress * 0.5);
      }
    } else if (adjustedTime < 0.8) {
      // Phase 2: White flash dissolve
      const dissolveProgress = (adjustedTime - 0.5) / 0.3;
      const eased = easeOutCubic(dissolveProgress);

      // Lerp to white
      const white = new THREE.Color(0xffffff);
      const dissolveColor = rainbowColors[Math.floor(Math.random() * rainbowColors.length)]
        .clone().lerp(white, eased);

      meshRef.current.material.color.copy(dissolveColor);
      meshRef.current.material.emissive.copy(dissolveColor);
      meshRef.current.material.emissiveIntensity = 3.5 * (1 - eased * 0.5);
      meshRef.current.material.opacity = 0.95 - eased * 0.3;

      // Scale up slightly as it dissolves
      meshRef.current.scale.setScalar(1 + eased * 0.15);

      if (glowRef.current) {
        glowRef.current.material.color.set(0xffffff);
        glowRef.current.material.opacity = 0.5 * (1 - eased);
        glowRef.current.scale.setScalar(1.5 + eased);
      }
    } else {
      // Phase 3: Smooth fade away
      const fadeProgress = (adjustedTime - 0.8) / 0.25;
      meshRef.current.material.opacity = 0.65 * (1 - fadeProgress);
      meshRef.current.material.emissiveIntensity = 2 * (1 - fadeProgress);
      meshRef.current.scale.setScalar(1.15 + fadeProgress * 0.1);

      if (glowRef.current) glowRef.current.visible = false;
    }
  });

  return (
    <group position={position}>
      <mesh ref={meshRef} visible={false}>
        <boxGeometry args={[0.9, 0.9, 0.4]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={0.8}
          transparent
          opacity={0.95}
          metalness={0.6}
          roughness={0.15}
        />
      </mesh>
      {/* Soft glow halo */}
      <mesh ref={glowRef} position={[0, 0, -0.05]} visible={false}>
        <circleGeometry args={[0.7, 16]} />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={0}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
};

// --- Rainbow Ripple: concentric color rings expanding from center ---
const RainbowRipple = ({ delay, colorIndex }) => {
  const ringRef = useRef();
  const startTime = useRef(null);

  const color = SPECTRUM_RAINBOW[colorIndex % SPECTRUM_RAINBOW.length];

  useFrame((state) => {
    if (!ringRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - delay;

    if (adjustedTime <= 0 || adjustedTime > 1.2) {
      ringRef.current.visible = false;
      return;
    }

    ringRef.current.visible = true;

    const progress = adjustedTime / 1.2;
    const easedScale = easeOutQuint(progress);

    // Expand smoothly
    const scale = easedScale * 22;
    ringRef.current.scale.setScalar(scale);

    // Fade: quick ramp up, slow fade out
    const fadeIn = Math.min(progress * 8, 1);
    const fadeOut = 1 - easeInOutQuad(progress);
    ringRef.current.material.opacity = 0.45 * fadeIn * fadeOut;
  });

  return (
    <mesh ref={ringRef} position={[0, 10, 0.3]} visible={false}>
      <ringGeometry args={[0.9, 1.0, 32]} />
      <meshBasicMaterial
        color={color}
        transparent
        opacity={0.45}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
};

// --- Spectral Spark: tiny prismatic particle that arcs and twinkles ---
const SpectralSpark = ({ startPosition, velocity, delay, color, life }) => {
  const meshRef = useRef();
  const startTime = useRef(null);

  useFrame((state) => {
    if (!meshRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - delay;

    if (adjustedTime <= 0 || adjustedTime > life) {
      meshRef.current.visible = false;
      return;
    }

    meshRef.current.visible = true;

    const progress = adjustedTime / life;
    const gravity = -8;

    // Smooth arc movement
    const t = adjustedTime;
    const x = startPosition[0] + velocity[0] * t;
    const y = startPosition[1] + velocity[1] * t + 0.5 * gravity * t * t;
    const z = startPosition[2] + velocity[2] * t;

    meshRef.current.position.set(x, y, z);

    // Twinkle effect — smooth sine pulse
    const twinkle = Math.sin(adjustedTime * 18 + startPosition[0] * 5) * 0.3 + 0.7;

    // Fade: appear quickly, fade slowly
    const fadeIn = Math.min(progress * 6, 1);
    const fadeOut = progress > 0.5 ? 1 - easeOutCubic((progress - 0.5) / 0.5) : 1;
    const opacity = twinkle * fadeIn * fadeOut;

    meshRef.current.material.opacity = opacity;

    // Slight scale pulse
    const scale = 0.08 + twinkle * 0.04;
    meshRef.current.scale.setScalar(scale);
  });

  return (
    <mesh ref={meshRef} position={startPosition} visible={false}>
      <circleGeometry args={[0.1, 8]} />
      <meshBasicMaterial
        color={color}
        transparent
        opacity={0.9}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </mesh>
  );
};

// --- Stardust: lingering tiny twinkling particles that drift and fade slowly ---
const StardustParticle = ({ startPosition, delay }) => {
  const meshRef = useRef();
  const startTime = useRef(null);
  const drift = useMemo(() => ({
    x: (Math.random() - 0.5) * 1.0,
    y: 1.2 + Math.random() * 1.5,
    spin: (Math.random() - 0.5) * 3,
    twinkleOffset: Math.random() * Math.PI * 2,
    twinkleSpeed: 12 + Math.random() * 10,
  }), []);

  const color = useMemo(() =>
    SPECTRUM_RAINBOW[Math.floor(Math.random() * SPECTRUM_RAINBOW.length)],
  []);

  useFrame((state) => {
    if (!meshRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - delay;

    if (adjustedTime <= 0 || adjustedTime > 2.5) {
      meshRef.current.visible = false;
      return;
    }

    meshRef.current.visible = true;

    const progress = adjustedTime / 2.5;

    // Gentle drift upward with slight wobble
    const wobble = Math.sin(adjustedTime * 2.5 + drift.twinkleOffset) * 0.25;
    meshRef.current.position.x = startPosition[0] + wobble + drift.x * progress;
    meshRef.current.position.y = startPosition[1] + adjustedTime * drift.y;
    meshRef.current.position.z = startPosition[2];

    // Slow rotation
    meshRef.current.rotation.z += drift.spin * 0.012;

    // Beautiful twinkling — multiple sine waves for organic feel
    const twinkle1 = Math.sin(adjustedTime * drift.twinkleSpeed + drift.twinkleOffset) * 0.35;
    const twinkle2 = Math.sin(adjustedTime * drift.twinkleSpeed * 0.7 + 1.5) * 0.15;
    const sparkle = 0.5 + twinkle1 + twinkle2;

    // Smooth fade envelope
    const fadeIn = Math.min(progress * 5, 1);
    const fadeOut = progress > 0.6 ? 1 - easeOutCubic((progress - 0.6) / 0.4) : 1;

    meshRef.current.material.opacity = sparkle * fadeIn * fadeOut * 0.85;

    // Gentle scale breathing
    const scale = 0.08 + sparkle * 0.04;
    meshRef.current.scale.setScalar(scale);
  });

  return (
    <mesh ref={meshRef} position={startPosition} visible={false}>
      <circleGeometry args={[0.08, 6]} />
      <meshBasicMaterial
        color={color}
        transparent
        opacity={0.85}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </mesh>
  );
};

// --- Chromatic Aberration Flash: RGB-split row flash ---
const ChromaticRowFlash = ({ row, delay }) => {
  const redRef = useRef();
  const greenRef = useRef();
  const blueRef = useRef();
  const whiteRef = useRef();
  const startTime = useRef(null);

  useFrame((state) => {
    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - delay;

    const refs = [redRef, greenRef, blueRef, whiteRef];

    if (adjustedTime <= 0 || adjustedTime > 0.65) {
      refs.forEach(r => { if (r.current) r.current.visible = false; });
      return;
    }

    const progress = adjustedTime / 0.65;

    // White base flash
    if (whiteRef.current) {
      whiteRef.current.visible = true;
      const whiteIntensity = Math.sin(progress * Math.PI);
      whiteRef.current.material.opacity = 0.7 * whiteIntensity;
    }

    // RGB split — red shifts left, blue shifts right, green stays center
    const splitAmount = Math.sin(progress * Math.PI) * 0.35;
    const splitOpacity = Math.sin(progress * Math.PI) * 0.5;

    if (redRef.current) {
      redRef.current.visible = true;
      redRef.current.position.x = -splitAmount;
      redRef.current.material.opacity = splitOpacity;
    }
    if (greenRef.current) {
      greenRef.current.visible = true;
      greenRef.current.material.opacity = splitOpacity * 0.7;
    }
    if (blueRef.current) {
      blueRef.current.visible = true;
      blueRef.current.position.x = splitAmount;
      blueRef.current.material.opacity = splitOpacity;
    }
  });

  const yPos = 20 - row - 0.5;

  return (
    <>
      {/* White base */}
      <mesh ref={whiteRef} position={[0, yPos, 0.25]} visible={false}>
        <planeGeometry args={[11, 1.1]} />
        <meshBasicMaterial
          color={0xffffff}
          transparent
          opacity={0.7}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>
      {/* Red channel - shifted left */}
      <mesh ref={redRef} position={[0, yPos, 0.26]} visible={false}>
        <planeGeometry args={[11, 1.1]} />
        <meshBasicMaterial
          color={'#FF3030'}
          transparent
          opacity={0.5}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>
      {/* Green channel - center */}
      <mesh ref={greenRef} position={[0, yPos, 0.26]} visible={false}>
        <planeGeometry args={[11, 1.1]} />
        <meshBasicMaterial
          color={'#32CD32'}
          transparent
          opacity={0.35}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>
      {/* Blue channel - shifted right */}
      <mesh ref={blueRef} position={[0, yPos, 0.26]} visible={false}>
        <planeGeometry args={[11, 1.1]} />
        <meshBasicMaterial
          color={'#4169FF'}
          transparent
          opacity={0.5}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>
    </>
  );
};

// --- Prism Burst: central prismatic explosion that refracts light outward ---
const PrismBurst = () => {
  const coreRef = useRef();
  const startTime = useRef(null);

  // Create 7 rainbow ring refs
  const ringRefs = useRef([]);

  useFrame((state) => {
    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;

    // Core white flash
    if (coreRef.current) {
      if (elapsed < 0.4) {
        coreRef.current.visible = true;
        const progress = elapsed / 0.4;
        const scale = 1 + easeOutCubic(progress) * 10;
        coreRef.current.scale.setScalar(scale);
        coreRef.current.material.opacity = 0.9 * (1 - easeOutCubic(progress));
      } else {
        coreRef.current.visible = false;
      }
    }
  });

  return (
    <>
      {/* Central white core flash */}
      <mesh ref={coreRef} position={[0, 10, 0.45]} visible={false}>
        <circleGeometry args={[0.6, 32]} />
        <meshBasicMaterial
          color={0xffffff}
          transparent
          opacity={0.9}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>
    </>
  );
};

const SpectrumTetrisFinisher = ({ onComplete, blocks = [] }) => {
  const groupRef = useRef();
  const startTime = useRef(Date.now());

  const allColors = useMemo(() => Object.values(TETROMINO_COLORS), []);

  // Get unique rows for effects
  const uniqueRows = useMemo(() => {
    if (blocks.length === 0) return [16, 17, 18, 19];
    const rows = [...new Set(blocks.map(b => b.row))];
    return rows.sort((a, b) => a - b);
  }, [blocks]);

  // Chromatic dissolve blocks — each block gets the dissolve treatment
  const dissolveBlocks = useMemo(() => {
    const sourceBlocks = blocks.length > 0 ? blocks : (() => {
      const fallback = [];
      for (let row = 16; row < 20; row++) {
        for (let col = 0; col < 10; col++) {
          fallback.push({
            row,
            col,
            colorClass: allColors[Math.floor(Math.random() * allColors.length)]
          });
        }
      }
      return fallback;
    })();

    return sourceBlocks.map((block) => {
      const x = block.col - 4.5;
      const y = 20 - block.row - 0.5;
      const color = typeof block.colorClass === 'string'
        ? getBlockColor(block.colorClass)
        : block.colorClass;

      // Stagger: beam sweeps left to right, so left blocks dissolve first
      const delay = (block.col / 10) * 0.18 + (uniqueRows.indexOf(block.row)) * 0.06;

      return { position: [x, y, 0.2], color, delay };
    });
  }, [blocks, allColors, uniqueRows]);

  // Spectral sparks — prismatic particles that arc outward
  const spectralSparks = useMemo(() => {
    const sparks = [];
    const sourceBlocks = blocks.length > 0 ? blocks : (() => {
      const fallback = [];
      for (let row = 16; row < 20; row++) {
        for (let col = 0; col < 10; col++) {
          fallback.push({ row, col });
        }
      }
      return fallback;
    })();

    sourceBlocks.forEach((block) => {
      const baseX = block.col - 4.5;
      const baseY = 20 - block.row - 0.5;

      // 2 sparks per block
      const sparkCount = 2;
      for (let s = 0; s < sparkCount; s++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 2 + Math.random() * 4;
        const upBias = 5 + Math.random() * 6;

        sparks.push({
          startPosition: [baseX + (Math.random() - 0.5) * 0.3, baseY, 0.35],
          velocity: [
            Math.cos(angle) * speed + baseX * 0.6,
            upBias,
            (Math.random() - 0.5) * 2,
          ],
          delay: 0.35 + (block.col / 10) * 0.18 + Math.random() * 0.08,
          color: SPECTRUM_RAINBOW[Math.floor(Math.random() * SPECTRUM_RAINBOW.length)],
          life: 1.2 + Math.random() * 0.6,
        });
      }
    });

    return sparks;
  }, [blocks]);

  // Rainbow ripples — concentric rings of spectrum colors
  const rainbowRipples = useMemo(() => {
    return SPECTRUM_RAINBOW.map((_, i) => ({
      delay: 0.05 + i * 0.09,
      colorIndex: i,
    }));
  }, []);

  // Prismatic beams — sweep across each row
  const prismaticBeams = useMemo(() => {
    return uniqueRows.map((row, i) => ({
      row,
      delay: i * 0.1,
    }));
  }, [uniqueRows]);

  // Stardust particles — lingering twinkles
  const stardustParticles = useMemo(() => {
    const particles = [];
    const count = 45;

    for (let i = 0; i < count; i++) {
      const row = uniqueRows[Math.floor(Math.random() * uniqueRows.length)];
      const x = (Math.random() - 0.5) * 12;
      const y = 20 - row - 0.5 + (Math.random() - 0.5) * 2;

      particles.push({
        startPosition: [x, y, 0.15],
        delay: 0.4 + Math.random() * 0.5,
      });
    }

    return particles;
  }, [uniqueRows]);

  useFrame(() => {
    const elapsed = (Date.now() - startTime.current) / 1000;

    if (elapsed > 3.2) {
      onComplete?.();
    }
  });

  return (
    <group ref={groupRef}>
      {/* Central prism burst */}
      <PrismBurst />

      {/* Rainbow ripples expanding outward */}
      {rainbowRipples.map((ripple, i) => (
        <RainbowRipple
          key={`ripple-${i}`}
          delay={ripple.delay}
          colorIndex={ripple.colorIndex}
        />
      ))}

      {/* Chromatic row flashes with RGB split */}
      {uniqueRows.map((row, i) => (
        <ChromaticRowFlash key={`flash-${row}`} row={row} delay={i * 0.07} />
      ))}

      {/* Prismatic beams sweeping across rows */}
      {prismaticBeams.map((beam, i) => (
        <PrismaticBeam key={`beam-${i}`} row={beam.row} delay={beam.delay} />
      ))}

      {/* Chromatic dissolve on each block */}
      {dissolveBlocks.map((block, i) => (
        <ChromaticDissolveBlock
          key={`dissolve-${i}`}
          position={block.position}
          color={block.color}
          delay={block.delay}
        />
      ))}

      {/* Spectral sparks arcing outward */}
      {spectralSparks.map((spark, i) => (
        <SpectralSpark
          key={`spark-${i}`}
          startPosition={spark.startPosition}
          velocity={spark.velocity}
          delay={spark.delay}
          color={spark.color}
          life={spark.life}
        />
      ))}

      {/* Stardust lingering and twinkling */}
      {stardustParticles.map((dust, i) => (
        <StardustParticle
          key={`dust-${i}`}
          startPosition={dust.startPosition}
          delay={dust.delay}
        />
      ))}
    </group>
  );
};

export default SpectrumTetrisFinisher;
